import moment, { type Moment } from "moment"
import { DATE_FORMAT } from "helpers/dates"
import { calendarDayDiff, relativeDayLabel } from "helpers/intraday"

export interface IntradayReportedPnl {
  totalReturn: number | null
  amount: number | null
  asOf: string | null
  isApplied: boolean | null
  basis: string | null
}

export interface IntradayToolbarTimes {
  positionsAsOf: string | null
  pricesAsOf: string | null
  currency: string | null
  reportedPnl: IntradayReportedPnl | null
}

export interface IntradayToolbarTimesProp {
  data: IntradayToolbarTimes | null
  qualifiers: string[]
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null
}

function readString(value: unknown): string | null {
  return typeof value === "string" ? value : null
}

function readNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null
}

function readBoolean(value: unknown): boolean | null {
  return typeof value === "boolean" ? value : null
}

function parseReportedPnl(value: unknown): IntradayReportedPnl | null {
  if (!isRecord(value)) return null
  return {
    totalReturn: readNumber(value.totalReturn),
    amount: readNumber(value.amount),
    asOf: readString(value.asOf),
    isApplied: readBoolean(value.isApplied),
    basis: readString(value.basis),
  }
}

export function parseIntradayToolbarTimes(simulation: unknown): IntradayToolbarTimes | null {
  if (!isRecord(simulation)) return null
  return {
    positionsAsOf: readString(simulation.positionsAsOf),
    pricesAsOf: readString(simulation.pricesAsOf),
    currency: readString(simulation.currency),
    reportedPnl: parseReportedPnl(simulation.reportedPnl),
  }
}

export function formatChipMoment(time: Moment, format: "LT" | "LTS", now: Moment): string {
  const relativeDay = relativeDayLabel(calendarDayDiff(time.format(DATE_FORMAT), now))
  return relativeDay === "today" ? time.format(format) : `${relativeDay} ${time.format(format)}`
}

function formatChipTime(value: string, format: "LT" | "LTS", now: Moment): string {
  return formatChipMoment(moment(value, moment.ISO_8601), format, now)
}

export function isReportedPnlFromLatestBatch({ positionsAsOf, reportedPnl }: IntradayToolbarTimes): boolean {
  if (!positionsAsOf || !reportedPnl?.asOf) return false
  return moment(reportedPnl.asOf, moment.ISO_8601).isSame(moment(positionsAsOf, moment.ISO_8601), "minute")
}

export interface ChipPart {
  label: string
  value: string
}

export function getPositionsChipParts(times: IntradayToolbarTimes, now: Moment = moment()): ChipPart[] {
  const { positionsAsOf, reportedPnl } = times
  if (!positionsAsOf) return [{ label: "Positions", value: "Start of day" }]
  if (!reportedPnl?.asOf) return [{ label: "Positions", value: formatChipTime(positionsAsOf, "LTS", now) }]
  if (isReportedPnlFromLatestBatch(times)) return [{ label: "Positions + P&L", value: formatChipTime(positionsAsOf, "LTS", now) }]
  return [
    { label: "Positions", value: formatChipTime(positionsAsOf, "LT", now) },
    { label: "P&L", value: formatChipTime(reportedPnl.asOf, "LT", now) },
  ]
}

export function getPositionsBubblePart({ positionsAsOf }: IntradayToolbarTimes, now: Moment = moment()): ChipPart {
  return { label: "Positions", value: positionsAsOf ? formatChipTime(positionsAsOf, "LTS", now) : "Start of day" }
}

export function getPricesChipParts(pricesAsOf: string | null, now: Moment = moment()): ChipPart[] | null {
  return pricesAsOf ? [{ label: "Performance", value: `through ~${formatChipTime(pricesAsOf, "LT", now)}` }] : null
}
